/**
 * BrainDumpTimeline — chronological list with date grouping + filter pills.
 *
 * A13-N12. Sub-view within BrainDumpPage (tab navigation).
 *
 * Props:
 *  - onNoteSelect: (noteId: string) => void
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { AnimatedList } from '../motion';

const PAGE_SIZE = 50;
const CAPTURE_ICONS = { text: '\u270D\uFE0F', voice: '\uD83C\uDFA4', photo: '\uD83D\uDCF7', mixed: '\uD83D\uDCDD', chat: '\uD83D\uDCAC' };

const API_PREFIX = '/api/v1/aif/brain-dump';

function getDateGroupLabel(dateStr, t) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  if (date >= today) return t('brainDump.today', 'Heute');
  if (date >= yesterday) return t('brainDump.yesterday', 'Gestern');
  if (date >= weekAgo) return t('brainDump.thisWeek', 'Diese Woche');
  return t('brainDump.older', '\u00C4lter');
}

function formatTime(dateStr) {
  try {
    return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function BrainDumpTimeline({ onNoteSelect }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filterCaptureType, setFilterCaptureType] = useState(null);
  const [filterDocumentType, setFilterDocumentType] = useState(null);

  const loadPage = useCallback(async (currentOffset, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(currentOffset),
      });
      if (filterCaptureType) params.append('capture_type', filterCaptureType);
      if (filterDocumentType) params.append('document_type', filterDocumentType);

      setError(null);
      const res = await apiClient.get(`${API_PREFIX}/notes?${params}`);
      const data = res.data ?? res;
      const newNotes = data.notes || [];

      if (append) {
        setNotes((prev) => [...prev, ...newNotes]);
      } else {
        setNotes(newNotes);
      }
      setHasMore(data.has_more);
    } catch (err) {
      setError(err.message || t('brainDump.loadError', 'Notizen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [filterCaptureType, filterDocumentType]);

  useEffect(() => {
    setOffset(0);
    loadPage(0, false);
  }, [loadPage]);

  function handleLoadMore() {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    loadPage(newOffset, true);
  }

  // Group notes by date
  const grouped = {};
  notes.forEach((n) => {
    const label = getDateGroupLabel(n.captured_at, t);
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(n);
  });

  const groupOrder = [
    t('brainDump.today', 'Heute'),
    t('brainDump.yesterday', 'Gestern'),
    t('brainDump.thisWeek', 'Diese Woche'),
    t('brainDump.older', '\u00C4lter'),
  ];

  return (
    <div className="space-y-4" data-testid="timeline-view">
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="filter-pills">
        {['text', 'voice', 'photo'].map((ct) => (
          <button
            key={ct}
            onClick={() => setFilterCaptureType(filterCaptureType === ct ? null : ct)}
            className={`text-xs px-3 py-1 rounded-full border transition min-h-[var(--aif-touch-min)] ${
              filterCaptureType === ct
                ? 'bg-[var(--aif-color-primary)] text-white border-[var(--aif-color-primary)]'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] border-[var(--aif-border-default)] hover:bg-[var(--aif-border-default)]'
            }`}
            data-testid={`filter-capture-${ct}`}
          >
            {CAPTURE_ICONS[ct]} {ct}
          </button>
        ))}
        {['receipt', 'invoice', 'observation'].map((dt) => (
          <button
            key={dt}
            onClick={() => setFilterDocumentType(filterDocumentType === dt ? null : dt)}
            className={`text-xs px-3 py-1 rounded-full border transition min-h-[var(--aif-touch-min)] ${
              filterDocumentType === dt
                ? 'bg-[var(--aif-color-warning)] text-white border-[var(--aif-color-warning)]'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] border-[var(--aif-border-default)] hover:bg-[var(--aif-border-default)]'
            }`}
            data-testid={`filter-doctype-${dt}`}
          >
            {dt}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded-lg text-sm" data-testid="timeline-error">
          {error}
        </div>
      )}

      {/* Content */}
      {loading && notes.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 animate-pulse h-16" />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="text-[var(--aif-text-muted)] text-sm text-center py-8" data-testid="timeline-empty">
          {t('brainDump.emptyState', 'Noch keine Notizen.')}
        </div>
      ) : (
        <div className="space-y-5">
          {groupOrder.map((group) => {
            if (!grouped[group] || grouped[group].length === 0) return null;
            return (
              <div key={group}>
                <h2
                  className="sticky top-0 z-10 bg-[var(--aif-bg-page)] py-1.5 px-1 text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide"
                  data-testid={`date-group-${group}`}
                >
                  {group}
                </h2>
                <AnimatedList className="space-y-2 mt-1">
                  {grouped[group].map((note) => (
                    <div
                      key={note.id}
                      onClick={() => onNoteSelect(note.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onNoteSelect(note.id)}
                      className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 cursor-pointer hover:shadow-sm transition"
                      data-testid={`timeline-note-${note.id}`}
                    >
                      <div className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)] mb-1">
                        <span>{CAPTURE_ICONS[note.capture_type] || '\uD83D\uDCDD'}</span>
                        <span>{formatTime(note.captured_at)}</span>
                        {note.location_label && <span>· {note.location_label}</span>}
                        {note.document_type && note.document_type !== 'note' && (
                          <span className="ml-auto text-[var(--aif-color-warning)]">{note.document_type}</span>
                        )}
                      </div>
                      <p className="text-sm text-[var(--aif-text-primary)] line-clamp-2">
                        {note.structured_summary || note.raw_text || (
                          <span className="text-[var(--aif-text-muted)] italic">
                            {t('brainDump.processing', 'Wird verarbeitet...')}
                          </span>
                        )}
                      </p>
                    </div>
                  ))}
                </AnimatedList>
              </div>
            );
          })}

          {hasMore && (
            <div className="text-center pt-2">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="text-sm text-[var(--aif-color-primary)] hover:underline disabled:opacity-50 min-h-[44px]"
                data-testid="load-more-btn"
              >
                {loading ? t('common.loading', 'L\u00E4dt...') : t('brainDump.loadMore', 'Mehr laden')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
